import { useState } from "react";
import { Menu, X, Compass, ChevronDown } from "lucide-react";

const SERVICES = [
  { label: "Unternehmensgründung (LLC & Corporation)", href: "/unternehmensgruendung-usa", id: "gruendung" },
  { label: "Immigration & Visa-Service", href: "/visumsberatung-usa", id: "immigration" },
  { label: "Auswandern & Relocation Florida", href: "/auswandern-florida", id: "relocation" },
  { label: "Beratung nach der Auswanderung", href: "/beratung-nach-auswanderung", id: "beratung" },
  { label: "Fixfertige Business-Ideen", href: "/business-ideen", id: "business-ideen" },
  { label: "Businessplan-Erstellung", href: "/businessplan-erstellung-usa", id: "businessplan" },
];

const NAV = [
  { label: "Leistungen", href: "/leistungen", id: "leistungen" },
  { label: "Visa", href: "/visa", id: "visa" },
  { label: "Ablauf", href: "/ablauf", id: "ablauf" },
  { label: "Über uns", href: "/ueber-uns", id: "ueber-uns" },
  { label: "FAQ", href: "/faq", id: "faq" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-line bg-sand"
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <a href="/" data-testid="brand-link" className="flex shrink-0 items-center gap-3 lg:mr-10">
          <span className="flex h-10 w-10 items-center justify-center bg-clay text-sand">
            <Compass className="h-5 w-5" strokeWidth={1.5} />
          </span>
          <span className="font-serif text-base leading-tight sm:text-lg">
            Florida Business <span className="italic text-clay">&amp;</span> Relocation
          </span>
        </a>

        <nav data-testid="main-nav" className="hidden items-center gap-6 lg:flex">
          <div className="group relative" data-testid="nav-leistungen-dropdown">
            <a
              href="/leistungen"
              data-testid="nav-link-leistungen"
              className="flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-ink-muted transition-colors hover:text-clay"
            >
              Leistungen
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" strokeWidth={1.5} />
            </a>
            <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-4 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
              <div className="w-80 border border-line bg-sand shadow-lg" data-testid="nav-leistungen-menu">
                {SERVICES.map((s) => (
                  <a
                    key={s.id}
                    href={s.href}
                    data-testid={`nav-service-${s.id}`}
                    className="block border-b border-line px-6 py-4 text-xs uppercase tracking-[0.12em] text-ink-muted transition-colors last:border-b-0 hover:bg-sand-deep hover:text-clay"
                  >
                    {s.label}
                  </a>
                ))}
                <a
                  href="/leistungen"
                  data-testid="nav-service-alle"
                  className="block bg-sand-deep px-6 py-4 text-xs uppercase tracking-[0.12em] text-clay transition-colors hover:text-clay-dark"
                >
                  Alle Leistungen im Überblick
                </a>
              </div>
            </div>
          </div>
          {NAV.filter((item) => item.id !== "leistungen").map((item) => (
            <a
              key={item.id}
              href={item.href}
              data-testid={`nav-link-${item.id}`}
              className="text-xs uppercase tracking-[0.15em] text-ink-muted transition-colors hover:text-clay"
            >
              {item.label}
            </a>
          ))}
        </nav>

        <a
          href="/kontakt"
          data-testid="header-cta-button"
          className="hidden bg-clay px-6 py-3 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark lg:inline-block"
        >
          Erstgespräch buchen
        </a>

        <button
          type="button"
          data-testid="mobile-menu-toggle"
          aria-label="Menü öffnen"
          onClick={() => setOpen((v) => !v)}
          className="text-ink lg:hidden"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="border-t border-line bg-sand lg:hidden">
          <nav className="flex flex-col px-6 py-6">
            <button
              type="button"
              data-testid="mobile-leistungen-toggle"
              aria-expanded={servicesOpen}
              onClick={() => setServicesOpen((v) => !v)}
              className="flex items-center justify-between border-b border-line py-4 text-left text-sm uppercase tracking-[0.15em] text-ink"
            >
              Leistungen
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${servicesOpen ? "rotate-180" : ""}`}
                strokeWidth={1.5}
              />
            </button>
            {servicesOpen && (
              <div data-testid="mobile-leistungen-menu" className="border-b border-line">
                {SERVICES.map((s) => (
                  <a
                    key={s.id}
                    href={s.href}
                    data-testid={`mobile-nav-service-${s.id}`}
                    onClick={() => setOpen(false)}
                    className="block border-b border-line py-3 pl-4 text-xs uppercase tracking-[0.12em] text-ink-muted"
                  >
                    {s.label}
                  </a>
                ))}
                <a
                  href="/leistungen"
                  data-testid="mobile-nav-service-alle"
                  onClick={() => setOpen(false)}
                  className="block py-3 pl-4 text-xs uppercase tracking-[0.12em] text-clay"
                >
                  Alle Leistungen im Überblick
                </a>
              </div>
            )}
            {NAV.filter((item) => item.id !== "leistungen").map((item) => (
              <a
                key={item.id}
                href={item.href}
                data-testid={`mobile-nav-link-${item.id}`}
                onClick={() => setOpen(false)}
                className="border-b border-line py-4 text-sm uppercase tracking-[0.15em] text-ink"
              >
                {item.label}
              </a>
            ))}
            <a
              href="/kontakt"
              data-testid="mobile-cta-button"
              onClick={() => setOpen(false)}
              className="mt-6 bg-clay px-6 py-4 text-center text-xs uppercase tracking-[0.2em] text-sand"
            >
              Erstgespräch buchen
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
